import React, { useEffect, useState, useRef } from 'react';
import './App.css';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

function App() {
  const [ticketStatus, setTicketStatus] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('Scan a ticket QR code');
  const [isScanning, setIsScanning] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");
  const qrScannerRef = useRef<Html5Qrcode | null>(null);

  const startScanner = async () => {
    try {
      const html5QrCode = new Html5Qrcode("reader");
      qrScannerRef.current = html5QrCode;
      
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        formatsToSupport: [ Html5QrcodeSupportedFormats.QR_CODE ]
      };

      await html5QrCode.start(
        { facingMode: facingMode },
        config,
        onScanSuccess,
        onScanFailure
      );
      setIsScanning(true);
    } catch (err) {
      console.error("Error starting scanner:", err);
      setMessage("Error starting camera. Please ensure permissions are granted.");
    }
  };

  const stopScanner = async () => {
    if (qrScannerRef.current && qrScannerRef.current.isScanning) {
      try {
        await qrScannerRef.current.stop();
        setIsScanning(false);
        setTorchOn(false);
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
  };

  const onScanSuccess = async (decodedText: string) => {
    await stopScanner();
    setMessage(`Checking ticket: ${decodedText}`);
    try {
      const response = await fetch(`/get_ticket_status?ticket_id=${decodedText}`);
      const data = await response.json();
      const status = data.status;

      if (status === "valid") {
        await fetch(`/invalidate_ticket?ticket_id=${decodedText}`);
        setTicketStatus("valid");
        setMessage("Ticket is VALID and has been invalidated.");
      } else if (status === "invalid") {
        setTicketStatus("invalid");
        setMessage("Ticket is INVALID.");
      } else {
        setTicketStatus("unknown");
        setMessage("Ticket status is UNKNOWN.");
      }
    } catch (err) {
      console.error("Error fetching ticket status:", err);
      setMessage("Error connecting to backend.");
    }
  };

  const onScanFailure = (error: any) => {
    // Too many failures are normal while scanning, so we don't log them usually
  };

  const toggleTorch = async () => {
    if (qrScannerRef.current && qrScannerRef.current.isScanning) {
      try {
        const newTorchState = !torchOn;
        await qrScannerRef.current.applyVideoConstraints({
          advanced: [{ torch: newTorchState } as any]
        });
        setTorchOn(newTorchState);
      } catch (err) {
        console.error("Error toggling torch:", err);
      }
    }
  };

  const switchCamera = async () => {
    const newFacingMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newFacingMode);
    if (isScanning) {
      await stopScanner();
      // Small timeout to allow state update and DOM cleanup
      setTimeout(startScanner, 100);
    }
  };

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  return (
    <div className="App">
      <header className="App-header">
        <h1>Ticket Scanner</h1>
        <div id="reader" style={{ width: '100%', maxWidth: '500px' }}></div>
        
        <div className="controls">
          {!isScanning ? (
            <button onClick={startScanner}>Start Scanning</button>
          ) : (
            <button onClick={stopScanner}>Stop Scanning</button>
          )}
          <button onClick={switchCamera}>Switch Camera ({facingMode})</button>
          <button onClick={toggleTorch} disabled={!isScanning}>
            {torchOn ? "Turn Torch Off" : "Turn Torch On"}
          </button>
        </div>

        <div className={`status-display ${ticketStatus}`}>
          <p>{message}</p>
          {ticketStatus && (
             <button onClick={() => {
               setTicketStatus(null);
               setMessage('Scan a ticket QR code');
               startScanner();
             }}>Scan Next</button>
          )}
        </div>
      </header>
    </div>
  );
}

export default App;
